/* Seitenleiste „Entities“: neue Entity (Klasse wählen, platzieren) und die Liste der Entities der Karte. */
import { createRoot } from 'react-dom/client';
import Cmd from '../../model/cmds.ts';
import Core from '../../core.ts';
import EntDefs from '../../model/entdefs.ts';
import type { Doc, Entity, Sel, Vec3 } from '../../model/types.ts';
import { useT } from '../hooks.ts';
import { ClassSelect } from '../class-options.tsx';
import { createNotifier, useNotifier } from '../notifier.ts';
import type { Notifier } from '../notifier.ts';

const MAX_ROWS = 300;

interface ViewLike { ax?: { h: number; v: number; d: number }; cx: number; cy: number }
export interface EntsApp {
  doc: Doc; sel: Sel; entClass: string; entDepth: number[]; snapOn: boolean; grid: number;
  activeView: ViewLike | null; v2: { top: ViewLike & { ax: { h: number; v: number; d: number } } };
  /** keine FGD mit der Karte verknüpft */
  fgdNone(): boolean;
  setSel(sel: Sel): void; fit(selection: boolean): void; placeEntity(o: Vec3, cls?: string): void; toEntity(cls: string): void; setTool(tool: string): void;
}
/** Filtertext, bleibt über das Neuzeichnen erhalten */
export interface EntsShared { entFilter: string }

const label = (e: Entity): string => e.kv.classname + (e.kv.targetname ? ' · ' + e.kv.targetname : '');

export function EntitiesPanel(props: { app: EntsApp; shared: EntsShared; notifier: Notifier }) {
  const t = useT();
  useNotifier(props.notifier);
  const { app, shared } = props;
  const cls = app.entClass, def = EntDefs.get(cls);
  const f = shared.entFilter.trim().toLowerCase();
  const rows = app.doc.ents.map((e, i) => ({ e, i })).filter(({ i }) => i > 0).filter(({ e }) => !f || label(e).toLowerCase().indexOf(f) >= 0);

  const place = () => { if (def.k === 'brush') app.toEntity(cls); else { app.entClass = cls; app.setTool('entity'); } };
  const atCenter = () => {
    const v = app.activeView && app.activeView.ax ? app.activeView : app.v2.top, ax = (v as { ax: { h: number; v: number; d: number } }).ax;
    const o: Vec3 = [0, 0, 0], g = app.snapOn ? app.grid : 1;
    o[ax.h] = Math.round(v.cx / g) * g; o[ax.v] = Math.round(v.cy / g) * g; o[ax.d] = app.entDepth[ax.d];
    app.entClass = cls; app.placeEntity(o);
  };
  const pressed = (e: Entity) => app.sel.e.has(e.id) || (e.brushes.length > 0 && e.brushes.every((b) => app.sel.b.has(b.id)));
  const select = (e: Entity, i: number) => {
    const ns = Cmd.emptySel();
    if (Cmd.isPointEnt(e, i)) ns.e.add(e.id); else e.brushes.forEach((b) => ns.b.add(b.id));
    app.setSel(ns);
  };

  return (
    <>
      <h3>{t('pn.new_entity')}</h3>
      {app.fgdNone() && <div className="warnbox" role="status">{t('pn.fgd_none_ents')}</div>}
      <ClassSelect value={cls} ariaLabel={t('pn.new_entity')} onPick={(c) => { app.entClass = c; props.notifier.notify(); }} onFree={(c) => { app.entClass = c; props.notifier.notify(); }} />
      <p className="small">{def.k === 'point' ? t('pn.ent_point') : t('pn.ent_brush')}</p>
      <div className="row">
        <button className="btn sm primary" title={t('pn.place_tip')} onClick={place}>{t('pn.place')}</button>
        <button className="btn sm" onClick={atCenter}>{t('pn.at_center')}</button>
      </div>
      <h3>{t('pn.in_map')}</h3>
      <input type="search" className="inp" style={{ width: '100%' }} placeholder={t('pn.filter')} value={shared.entFilter} onChange={(e) => { shared.entFilter = e.target.value; props.notifier.notify(); }} />
      <div className="list">
        {rows.slice(0, MAX_ROWS).map(({ e, i }) => {
          const o = Core.parseVec(e.kv.origin);
          return (
            <button key={e.id} className="li" data-id={e.id} aria-pressed={pressed(e)} onClick={() => select(e, i)} onDoubleClick={() => app.fit(false)}>
              <span className="dot" style={{ background: EntDefs.colorFor(e.kv.classname) }} />
              <span>{label(e)}</span>
              <span className="dim">{e.brushes.length ? e.brushes.length + ' B' : (o ? o.map((x) => Math.round(x)).join(' ') : '')}</span>
            </button>
          );
        })}
        {!rows.length && <p className="small" style={{ padding: 8 }}>{t('pn.no_ents')}</p>}
      </div>
      <p className="small">{rows.length > MAX_ROWS ? t('pn.first300') : t('pn.dblclick')}</p>
    </>
  );
}

export function mountEntitiesPanel(el: HTMLElement, app: EntsApp, shared: EntsShared): { refresh(): void; unmount(): void } {
  const notifier = createNotifier();
  const root = createRoot(el);
  el.textContent = '';
  root.render(<EntitiesPanel app={app} shared={shared} notifier={notifier} />);
  return { refresh: notifier.notify, unmount: () => root.unmount() };
}
